import { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import TimeSlot from "src/app/models/screens/availabilityTimeslot.model";
import BookingRequest, {
  RequestManagementUser,
  RequestStatus,
} from "src/app/models/screens/requestManagement.model";
import {
  acceptBookingRequest,
  createBookingRequest,
  createEmergencyBookingRequest,
  getParentChildren,
  getReceivedBookingRequests,
  getUserFreeTimeslots,
  rejectBookingRequest,
  searchUsers,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

const AvailabilityRequestManagementScreenViewModel = () => {
  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [userSearchText, setUserSearchText] = useState<string>("");
  const [isSearchingUsers, setIsSearchingUsers] = useState<boolean>(false);
  const [searchedUsers, setSearchedUsers] = useState<RequestManagementUser[]>(
    [],
  );
  const [selectedUser, setSelectedUser] =
    useState<RequestManagementUser | null>(null);
  const [selectedUserDate, setSelectedUserDate] = useState<Dayjs | null>(null);
  const [selectedUserFreeTimeslots, setSelectedUserFreeTimeslots] = useState<
    TimeSlot[]
  >([]);
  const [isLoadingSelectedUserTimeslots, setIsLoadingSelectedUserTimeslots] =
    useState<boolean>(false);
  const [selectedBookableSlot, setSelectedBookableSlot] =
    useState<TimeSlot | null>(null);
  const [requestDescription, setRequestDescription] = useState<string>("");
  const [receivedRequests, setReceivedRequests] = useState<BookingRequest[]>(
    [],
  );
  const [isLoadingReceivedRequests, setIsLoadingReceivedRequests] =
    useState<boolean>(false);
  const [isEmergencyRequestModalOpen, setIsEmergencyRequestModalOpen] =
    useState<boolean>(false);
  const [isEmergencyRequestButtonLoading, setIsEmergencyRequestButtonLoading] =
    useState<boolean>(false);
  const [emergencyRequestDescription, setEmergencyRequestDescription] =
    useState<string>("");
  const [userRole, setUserRole] = useState<string | undefined>(undefined);
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [selectedRequestOwnerId, setSelectedRequestOwnerId] =
    useState<string>("you");
  const [adminRequestUsers, setAdminRequestUsers] = useState<
    RequestManagementUser[]
  >([]);
  const [isLoadingAdminRequestUsers, setIsLoadingAdminRequestUsers] =
    useState<boolean>(false);
  const selectedUserFullName = useMemo(() => {
    if (!selectedUser) return "";
    return `${selectedUser.firstName} ${selectedUser.lastName}`;
  }, [selectedUser]);

  const isViewingAnotherUsersRequestsAsAdmin =
    userRole === "Admin" &&
    selectedTab === 1 &&
    selectedRequestOwnerId !== "you";

  const getCurrentChildId = useCallback((): string | undefined => {
    if (userRole !== "Parent") return undefined;
    return selectedRequestOwnerId === "you"
      ? undefined
      : selectedRequestOwnerId;
  }, [selectedRequestOwnerId, userRole]);

  const getCurrentAdminReceivedRequestsUserId = useCallback(():
    | string
    | undefined => {
    if (userRole !== "Admin") return undefined;
    if (selectedTab !== 1) return undefined;
    return selectedRequestOwnerId === "you"
      ? undefined
      : selectedRequestOwnerId;
  }, [selectedRequestOwnerId, selectedTab, userRole]);

  useEffect(() => {
    const savedUserRole: string | null = localStorage.getItem(
      localStorageConstants.ROLE,
    );
    if (!savedUserRole) return;
    setUserRole(savedUserRole);
  }, []);

  const loadParentChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setChildren(response.children ?? []);
  }, []);

  const loadAdminRequestUsers = useCallback(async () => {
    setIsLoadingAdminRequestUsers(true);
    const response = await searchUsers("");
    setIsLoadingAdminRequestUsers(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setAdminRequestUsers(response.users ?? []);
  }, []);

  const loadUsers = useCallback(async (query: string, childId?: string) => {
    setIsSearchingUsers(true);
    const response = await searchUsers(query, childId);
    setIsSearchingUsers(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setSearchedUsers(response.users ?? []);
  }, []);

  const loadReceivedRequests = useCallback(
    async (childId?: string, userId?: string) => {
      setIsLoadingReceivedRequests(true);
      const response = await getReceivedBookingRequests(childId, userId);
      setIsLoadingReceivedRequests(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      setReceivedRequests(response.requests ?? []);
    },
    [],
  );

  const loadSelectedUserFreeTimeslots = useCallback(
    async (userId: string, isoDate: string, childId?: string) => {
      setIsLoadingSelectedUserTimeslots(true);
      const response = await getUserFreeTimeslots(userId, isoDate, childId);
      setIsLoadingSelectedUserTimeslots(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      const timeslots = [...(response.freeTimeslots ?? [])].sort(
        (a, b) => a.from - b.from,
      );
      setSelectedUserFreeTimeslots(timeslots);
    },
    [],
  );

  useEffect(() => {
    if (userRole === "Parent") {
      loadParentChildren();
      return;
    }

    if (userRole === "Admin") {
      loadAdminRequestUsers();
    }
  }, [loadAdminRequestUsers, loadParentChildren, userRole]);

  const minutesToLabel = (minutes: number): string => {
    const hours: number = Math.floor(minutes / 60);
    const mins: number = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const showSuccess = (message: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  useEffect(() => {
    if (!userRole) return;

    loadUsers("", getCurrentChildId());
  }, [userRole, selectedRequestOwnerId, loadUsers, getCurrentChildId]);

  useEffect(() => {
    if (!userRole) return;
    const timeout = setTimeout(() => {
      loadUsers(userSearchText.trim(), getCurrentChildId());
    }, 300);
    return () => clearTimeout(timeout);
  }, [
    userSearchText,
    selectedRequestOwnerId,
    userRole,
    loadUsers,
    getCurrentChildId,
  ]);

  useEffect(() => {
    if (!userRole || userRole === "Guest") return;

    loadReceivedRequests(
      getCurrentChildId(),
      getCurrentAdminReceivedRequestsUserId(),
    );
  }, [
    userRole,
    selectedRequestOwnerId,
    selectedTab,
    loadReceivedRequests,
    getCurrentChildId,
    getCurrentAdminReceivedRequestsUserId,
  ]);

  useEffect(() => {
    if (!selectedUser || !selectedUserDate) {
      setSelectedUserFreeTimeslots([]);
      return;
    }
    loadSelectedUserFreeTimeslots(
      selectedUser.id,
      selectedUserDate.format("YYYY-MM-DD"),
      getCurrentChildId(),
    );
  }, [
    selectedUser,
    selectedUserDate,
    selectedRequestOwnerId,
    loadSelectedUserFreeTimeslots,
    getCurrentChildId,
  ]);

  useEffect(() => {
    const compareRequests = (a: BookingRequest, b: BookingRequest) => {
      if (a.status === "Pending" && b.status !== "Pending") return -1;
      if (a.status !== "Pending" && b.status === "Pending") return 1;
      if (a.status === "Pending" && b.status === "Pending") {
        if (a.emergency && !b.emergency) return -1;
        if (!a.emergency && b.emergency) return 1;
      }
      if (a.date < b.date) return -1;
      if (a.date > b.date) return 1;
      return a.from - b.from;
    };

    const isAlreadySorted = receivedRequests.every((request, index, arr) => {
      if (index === 0) return true;
      return compareRequests(arr[index - 1], request) <= 0;
    });
    if (!isAlreadySorted) {
      setReceivedRequests((prev) => [...prev].sort(compareRequests));
    }
  }, [receivedRequests]);

  const onSelectedRequestOwnerChanged = (ownerId: string) => {
    setSelectedRequestOwnerId(ownerId);

    if (selectedTab === 1) {
      setReceivedRequests([]);
      return;
    }

    setSelectedUser(null);
    setSelectedUserDate(null);
    setSelectedUserFreeTimeslots([]);
    setSelectedBookableSlot(null);
    setRequestDescription("");
  };

  const onTabChanged = (_event: React.SyntheticEvent, value: number) => {
    setSelectedTab(value);
  };

  const onUserSelected = (user: RequestManagementUser) => {
    setSelectedUser(user);
    setSelectedUserDate(null);
    setSelectedUserFreeTimeslots([]);
    setSelectedBookableSlot(null);
    setRequestDescription("");
  };

  const onSelectedUserDateChanged = (date: Dayjs | null) => {
    setSelectedUserDate(date);
    setSelectedBookableSlot(null);
  };

  const onBookableSlotSelected = (slot: TimeSlot) => {
    setSelectedBookableSlot(slot);
  };

  const onSubmitBookingRequest = async () => {
    if (!selectedUser) {
      showError("Please select a user.");
      return;
    }
    if (!selectedUserDate) {
      showError("Please select a date.");
      return;
    }
    if (!selectedBookableSlot) {
      showError("Please select a timeslot.");
      return;
    }
    if (!requestDescription.trim()) {
      showError("Please provide a description.");
      return;
    }
    const response = await createBookingRequest(
      selectedUser.id,
      selectedUserDate.format("YYYY-MM-DD"),
      selectedBookableSlot.from,
      selectedBookableSlot.to,
      requestDescription.trim(),
      getCurrentChildId(),
    );
    if (!response.success) {
      showError(response.error);
      return;
    }
    showSuccess("Booking request created successfully.");
    setSelectedBookableSlot(null);
    setRequestDescription("");
    await loadSelectedUserFreeTimeslots(
      selectedUser.id,
      selectedUserDate.format("YYYY-MM-DD"),
      getCurrentChildId(),
    );
  };

  const onAcceptRequest = async (requestId: string) => {
    const response = await acceptBookingRequest(requestId, getCurrentChildId());

    if (!response.success) {
      showError(response.error);
      return;
    }
    setReceivedRequests((prev) =>
      prev.map((request) =>
        response.updatedRequests.some(
          (rawRequest: any) => rawRequest.id === request.id,
        )
          ? {
              ...request,
              status: request.id === requestId ? "Accepted" : "Rejected",
            }
          : request,
      ),
    );
    showSuccess("Request accepted successfully.");
  };

  const onRejectRequest = async (requestId: string) => {
    const response = await rejectBookingRequest(requestId, getCurrentChildId());

    if (!response.success) {
      showError(response.error);
      return;
    }
    if (response.updatedRequest) {
      setReceivedRequests((prev) =>
        prev.map((request) =>
          request.id === response.updatedRequest.id
            ? { ...request, status: "Rejected" }
            : request,
        ),
      );
    } else {
      setReceivedRequests((prev) =>
        prev.map((request) =>
          request.id === requestId
            ? { ...request, status: "Rejected" }
            : request,
        ),
      );
    }
    showSuccess("Request rejected successfully.");
  };

  const getStatusChipColor = (
    status: RequestStatus,
  ): "warning" | "success" | "error" => {
    if (status === "Accepted") return "success";
    if (status === "Rejected") return "error";
    return "warning";
  };

  const closeEmergencyRequestModal = () => {
    setIsEmergencyRequestModalOpen(false);
    setEmergencyRequestDescription("");
  };

  const openEmergencyRequestModal = () => {
    setIsEmergencyRequestModalOpen(true);
  };

  const onComposeEmergencyRequestConfirmed = async () => {
    if (emergencyRequestDescription === "") {
      showError("You have to provide a description for your request");
      return;
    }
    if (!selectedUser) {
      showError("Select a user first");
      return;
    }
    setIsEmergencyRequestButtonLoading(true);
    const now: Date = new Date();
    const response = await createEmergencyBookingRequest(
      selectedUser.id,
      now.toISOString().split("T")[0],
      now.getHours() * 60 + now.getMinutes(),
      emergencyRequestDescription,
      getCurrentChildId(),
    );
    setIsEmergencyRequestButtonLoading(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    closeEmergencyRequestModal();
    showSuccess("Emergency request successfully composed");
  };

  return {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    selectedTab,
    onTabChanged,
    userSearchText,
    setUserSearchText,
    isSearchingUsers,
    searchedUsers,
    selectedUser,
    selectedUserFullName,
    onUserSelected,
    selectedUserDate,
    onSelectedUserDateChanged,
    selectedUserFreeTimeslots,
    isLoadingSelectedUserTimeslots,
    selectedBookableSlot,
    onBookableSlotSelected,
    requestDescription,
    setRequestDescription,
    onSubmitBookingRequest,
    receivedRequests,
    isLoadingReceivedRequests,
    onAcceptRequest,
    onRejectRequest,
    minutesToLabel,
    getStatusChipColor,
    isEmergencyRequestModalOpen,
    closeEmergencyRequestModal,
    openEmergencyRequestModal,
    onComposeEmergencyRequestConfirmed,
    isEmergencyRequestButtonLoading,
    emergencyRequestDescription,
    setEmergencyRequestDescription,
    userRole,
    children,
    isLoadingChildren,
    selectedRequestOwnerId,
    onSelectedRequestOwnerChanged,
    adminRequestUsers,
    isLoadingAdminRequestUsers,
    isViewingAnotherUsersRequestsAsAdmin,
  };
};

export default AvailabilityRequestManagementScreenViewModel;
